import { Localized } from "@fluent/react";
import {
    Alert,
    Box,
    ButtonGroup,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Skeleton,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, Fragment, ReactNode, useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { unwrapResult } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { AppId, AppTextFieldHandler } from "../../app/appTypes";
import { Urs } from "./UrsSlice";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { selectStructureNodeById } from "../device/structureSlice";
import { loadSettingsForCompany } from "../systemSettings/systemDocumentSettings/SystemDocumentSettingsApi";
import { selectContextStructure } from "../structure/contextStructureSlice";
import { DocumentNumberTextField } from "../documents/controls/DocumentNumberTextField";
import { StructureTreeSelect } from "../structure/StructureTreeSelect";
import { Help } from "../dialogs/HelpDialog";
import { createDynamicURS } from "./dynamicUrsSlice";
import { ClientDocumentTemplateSelectField } from "../documents/templates/ClientDocumentTemplateSelectField";
import { UrsRequirementPackagesSelect, UrsRequirementPackage } from "./UrsRequirementPackagesSelect";
import { LoadStructure } from "../../app/AppDataLoader";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { SaveButton } from "../../app/components/SaveButton";
import { CancelButton } from "../../app/components/CancelButton";

const RequiredError = () => (
    <Localized id="field-is-required">
        <span>_Pole jest wymagane</span>
    </Localized>
);

type CreateFromOptions = "Empty" | "Template";
type UrsNumberingMode = "ONLY_SIMPLE_REQ" | "UNIQUE_REQ" | "DECIMAL_HIERARCHICAL";

interface CreateDynamicUrsDialogProps {
    open?: boolean;
    onClose?: () => void;
    onCreated?: (urs: Urs) => void;
}

interface SectionProps {
    title: ReactNode;
    children: ReactNode;
}

const DialogSection = ({ title, children }: SectionProps) => {
    return (
        <Box
            sx={(theme) => ({
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
            })}
        >
            <Box
                sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    px: 1.5,
                    py: 0.75,
                    backgroundColor: theme.palette.grey[100],
                    borderBottom: `1px solid ${theme.palette.divider}`,
                })}
            >
                <ArrowRightIcon fontSize="small" />

                <Typography
                    variant="subtitle2"
                    sx={{
                        fontWeight: 600,
                        color: "text.secondary",
                        letterSpacing: 0.2,
                    }}
                >
                    {title}
                </Typography>
            </Box>

            <Box
                sx={{
                    p: 2,
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export const CreateDynamicUrsDialog = ({ open = true, onClose, onCreated }: CreateDynamicUrsDialogProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { search } = useLocation();

    const params = new URLSearchParams(search);
    const processId = params.has("processId") ? params.get("processId") : "";

    const { data: contextStructure } = useAppSelector(selectContextStructure);
    const contextStructureId = contextStructure && contextStructure.mode === "manual" ? contextStructure.structId : "";
    const structure = useAppSelector((state) => selectStructureNodeById(state, contextStructureId));

    const [deviceTypeId, setDeviceTypeId] = useState<AppId | undefined>(
        structure && structure.subStructureIds && structure.subStructureIds.length === 0 ? structure.id : ""
    );

    const [ursNumber, setUrsNumber] = useState("");
    const [ursTitle, setUrsTitle] = useState("");
    const [language, setLanguage] = useState("PL");
    const [selectedOption, setSelectedOption] = useState<CreateFromOptions>("Empty");
    const [templateId, setTemplateId] = useState<string>("");
    const [numberingMode, setNumberingMode] = useState<UrsNumberingMode>("UNIQUE_REQ");
    const [selectedRequirementPackage, setSelectedRequirementPackage] = useState<UrsRequirementPackage | undefined>(undefined);
    const [requirementSource, setRequirementSource] = useState<"EMPTY" | "PACKAGE">("EMPTY");

    const requirementPackageId = selectedRequirementPackage ? String(selectedRequirementPackage.id) : "";

    const [saving, setSaving] = useState(false);
    const [validationErrors, setValidationErrors] = useState(false);
    const [serverError, setServerError] = useState("");
    const [uiAlert, setUiAlert] = useState("");

    const ursTitleError = validationErrors && ursTitle === "";
    const packageError = validationErrors && requirementSource === "PACKAGE" && requirementPackageId === "";
    const templateStructureError = selectedOption === "Template" && !deviceTypeId;
    const templateRequiredError = validationErrors && selectedOption === "Template" && templateId === "";

    useEffect(() => {
        dispatch(loadSettingsForCompany(`?documentCode=URS`))
            .then(unwrapResult)
            .then((response) => {
                const numMode = response.find((f) => f.name === "NUMBERING_MODE");
                if (numMode !== undefined) {
                    setNumberingMode(numMode.value as UrsNumberingMode);
                }
            })
            .catch(() => undefined);
    }, [dispatch]);

    const handleDeviceTypeIdChange = useCallback((structureId) => {
        setDeviceTypeId(structureId);

        if (structureId) {
            setTemplateId("");
            setUiAlert("");
        }
    }, []);

    const handleModeChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const mode = event.target.value as CreateFromOptions;
        setSelectedOption(mode);
    }, []);

    const handleUrsTitleChange: AppTextFieldHandler = useCallback((e) => {
        setUrsTitle(e.target.value);
    }, []);

    const handleUrsNumberChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setUrsNumber(e.target.value);
    };

    const handleCloseButtonClick = useCallback(() => {
        if (onClose) {
            onClose();
            return;
        }
    }, [onClose]);

    const handleSaveButtonClick = async () => {
        setValidationErrors(true);
        setUiAlert("");
        setServerError("");

        if (selectedOption === "Template" && !deviceTypeId) {
            return;
        }

        if (!deviceTypeId || !ursNumber || !ursTitle) {
            return;
        }

        if (requirementSource === "PACKAGE" && requirementPackageId === "") {
            return;
        }

        if (selectedOption === "Template" && templateId === "") {
            return;
        }

        setSaving(true);
        try {
            const result = await dispatch(createDynamicURS({
                createFrom: selectedOption,
                numberingMode,
                deviceTypeId,
                ursNumber,
                title: ursTitle,
                structureTemplateId: templateId,
                requirementPackageId: requirementPackageId,
                ursId: undefined,
                purpose: "",
                area: "",
                description: "",
                language,
                processId,
            }));

            if (createDynamicURS.fulfilled.match(result)) {
                const newUrs = result.payload as Urs;
                dispatch(showSuccess("saved"));
                if (onCreated) {
                    onCreated(newUrs);
                } else {
                    navigate(`/document/edit/${newUrs.id}?structureId=${deviceTypeId ?? ""}`, { replace: true });
                }
            } else {
                let errorMsg = "error";
                if (result.payload && result.payload.kind === "http") {
                    const problem = result.payload.problem;
                    if (problem) {
                        errorMsg = problem.title;
                    }
                }
                setServerError(errorMsg);
                dispatch(showError(errorMsg));
            }
        } catch {
            setServerError("error");
            dispatch(showError("error"));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} fullWidth maxWidth="md">
            <DialogTitle>
                <Localized id="urs-create">
                    <span>_Tworzenie URS</span>
                </Localized>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="base-data">
                                    <span>_Dane podstawowe</span>
                                </Localized>
                            }
                        >
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <LoadStructure component={<Skeleton variant="text" />}>
                                        <StructureTreeSelect
                                            defaultStructureId={deviceTypeId}
                                            onStructureSelected={handleDeviceTypeIdChange}
                                            canSelectNonLeaf={false}
                                        />
                                    </LoadStructure>
                                </Grid>

                                <Grid item xs={8}>
                                    <DocumentNumberTextField
                                        fullWidth={true}
                                        required={true}
                                        value={ursNumber}
                                        onChange={handleUrsNumberChange}
                                        label={
                                            <Localized id="urs-number">
                                                <span>_Numer URS</span>
                                            </Localized>
                                        }
                                        documentType={"URS"}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <FormControl fullWidth>
                                        <InputLabel id="app-language-label">
                                            <Localized id="document-language">
                                                <span>_Język</span>
                                            </Localized>
                                        </InputLabel>
                                        <Select
                                            labelId="app-language-label"
                                            id="app-language"
                                            value={language}
                                            onChange={(e) => setLanguage(e.target.value)}
                                            label={
                                                <Localized id="document-language">
                                                    <span>_Język</span>
                                                </Localized>
                                            }
                                            fullWidth
                                        >
                                            <MenuItem value={"PL"}>PL</MenuItem>
                                            <MenuItem value={"EN"}>EN</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <TextField
                                        helperText={ursTitleError && <RequiredError />}
                                        required
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        value={ursTitle}
                                        error={ursTitleError}
                                        onChange={handleUrsTitleChange}
                                        label={
                                            <Localized id="urs-title">
                                                <span>_Tytuł</span>
                                            </Localized>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </DialogSection>
                    </Grid>

                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="document-structure-template">
                                    <span>_Document structure template</span>
                                </Localized>
                            }
                        >
                            <RadioGroup row value={selectedOption} onChange={handleModeChange}>
                                <FormControlLabel
                                    value="Empty"
                                    control={<Radio size="small" />}
                                    sx={{ mr: 2 }}
                                    label={
                                        <Typography variant="body2">
                                            <Localized id="document-creation-mode-empty">
                                                <span>_Pusty</span>
                                            </Localized>
                                        </Typography>
                                    }
                                />
                                <FormControlLabel
                                    value="Template"
                                    control={<Radio size="small" />}
                                    label={
                                        <Typography variant="body2">
                                            <Localized id="document-creation-mode-template">
                                                <span>_template</span>
                                            </Localized>
                                        </Typography>
                                    }
                                />
                            </RadioGroup>

                            {selectedOption === "Template" && (
                                <Box sx={{ mt: 2 }}>
                                    {deviceTypeId ? (
                                        <FormControl fullWidth error={templateRequiredError}>
                                            <ClientDocumentTemplateSelectField
                                                structureId={Number(deviceTypeId)}
                                                documentType="URS"
                                                value={templateId}
                                                language={language}
                                                filterStructureId={Number(deviceTypeId)}
                                                showFiltersTab={true}
                                                onChange={(id) => setTemplateId(id)}
                                            />
                                            {templateRequiredError && (
                                                <FormHelperText>
                                                    <Localized id="document-template-select">
                                                        <span>_Wybierz szablon</span>
                                                    </Localized>
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    ) : (
                                        <FormControl fullWidth error>
                                            <TextField
                                                fullWidth
                                                disabled
                                                label={<Localized id="menu-document-templates">
                                                    <span>_template</span>
                                                </Localized>}
                                                value=""
                                            />
                                            <FormHelperText>
                                                <Localized id="document-structure-needed-for-template-warning">
                                                    <span>_structure needed.</span>
                                                </Localized>
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                </Box>
                            )}
                        </DialogSection>
                    </Grid>

                    <Grid item xs={12}>
                        <DialogSection
                            title={
                                <Localized id="urs-requirement-settings">
                                    <span>_Ustawienia wymagań</span>
                                </Localized>
                            }
                        >
                            <Box sx={{ mb: 2 }}>

                                <RadioGroup
                                    row
                                    value={requirementSource}
                                    onChange={(e) => setRequirementSource(e.target.value as "EMPTY" | "PACKAGE")}
                                >
                                    <FormControlLabel
                                        value="EMPTY"
                                        control={<Radio size="small" />}
                                        label={
                                            <Typography variant="body2">
                                                <Localized id="urs-requirements-do-not-create">
                                                    <span>_Puste wymagania</span>
                                                </Localized>
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="PACKAGE"
                                        control={<Radio size="small" />}
                                        label={
                                            <Typography variant="body2">
                                                <Localized id="urs-requirements-source-package">
                                                    <span>_Z paczki wymagań</span>
                                                </Localized>
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                            </Box>

                            <Grid container spacing={2}>
                                {requirementSource === "EMPTY" && (
                                    <Grid item xs={12}>
                                        <TextField
                                            value={numberingMode}
                                            fullWidth
                                            select
                                            label={
                                                <Fragment>
                                                    <Localized id="urs-numbering-method">
                                                        <span>Sposób numerowania</span>
                                                    </Localized>
                                                    <Help id="7e0300fb-e1cf-4810-aab7-183f5e68d975" />
                                                </Fragment>
                                            }
                                            onChange={(e) => setNumberingMode(e.target.value as UrsNumberingMode)}
                                        >
                                            <MenuItem value={"UNIQUE_REQ"}>
                                                <Localized id="urs-unique">
                                                    <span>_Unikalne</span>
                                                </Localized>
                                            </MenuItem>
                                            <MenuItem value={"DECIMAL_HIERARCHICAL"}>
                                                <Localized id="urs-hierarchical">
                                                    <span>_Hierarchiczne</span>
                                                </Localized>
                                            </MenuItem>
                                        </TextField>
                                    </Grid>
                                )}

                                {requirementSource === "PACKAGE" && (
                                    <Grid item xs={12}>
                                        <UrsRequirementPackagesSelect
                                            value={requirementPackageId}
                                            language={language}
                                            structureId={deviceTypeId}
                                            onChange={(pkg) => setSelectedRequirementPackage(pkg)}
                                            error={packageError}
                                            showFilters={true}
                                            required
                                        />
                                    </Grid>
                                )}
                            </Grid>
                        </DialogSection>
                    </Grid>
                </Grid>

                {uiAlert && (
                    <Alert
                        sx={{ mt: 2, position: "sticky", bottom: 0, m: 0, borderRadius: 0 }}
                        severity="warning"
                        onClose={() => setUiAlert("")}
                    >
                        <span>{uiAlert}</span>
                    </Alert>
                )}

                {serverError && (
                    <Alert
                        sx={{ mt: 2, position: "sticky", bottom: 0, m: 0, borderRadius: 0 }}
                        severity="error"
                        onClose={() => setServerError("")}
                    >
                        <Localized id={serverError}>
                            <span>_Server error</span>
                        </Localized>
                    </Alert>
                )}
            </DialogContent>

            <DialogActions>
                <ButtonGroup variant="contained" color="secondary">
                    <SaveButton loading={saving} onClick={handleSaveButtonClick} />
                    <CancelButton loading={saving} onClick={handleCloseButtonClick} />
                </ButtonGroup>
            </DialogActions>
        </Dialog>
    );
};